import React, { useState, useEffect, useMemo } from 'react';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';
import { useParams } from 'react-router-dom';
import LoadingSpinnerModal from '../../Components/LoadingSpinnerModal';
import atlasLogo from '../../Assets/images/AtlasLogo.png';
import niarLogo from '../../Assets/images/NIARLogo.png';
import Milestones from './Components/milestones';
import Costs from './Components/costs';
import Hours from './Components/hours';
import Team from './Components/team';
import useFetchWorkPlans from '../../Hooks/dataFetcher/useFetchWorkPlans';
import { apiBaseURL } from '../../Utils/ProjectConstants';
import './styles.css';

//replace with a request
const allBillingCodes = [
    '1.1', '1.2', '1.3', '1.4',
    '2.1', '2.2', '2.3', '2.4', '2.5', '2.6', '2.7',
    '3.1', '3.2', '3.3', '3.4', '3.5', '3.6', '3.7', '3.8', '3.9', '3.10',
    '4.1', '4.2', '4.3', '4.4', '4.5', '4.6', '4.7', '4.8',
    '5.1', '5.2', '5.3', '5.6'
]

const useDate = () => {
    const locale = 'en-US',
        [getDate, setDate] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => {
            setDate(new Date());
        }, 60 * 1000);
        return () => {
            clearInterval(timer);
        }
    }, []);
    const date = `${getDate.toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric' })}\n\n`;
    const time = getDate.toLocaleTimeString(locale, { hour: 'numeric', hour12: true, minute: 'numeric' });

    return {
        date,
        time
    };
};

export default function ResearchDashboard() {
    const
        curr = new Date(),
        lastCurr = new Date(),
        first = (curr.getDate() - curr.getDay() + 1) - 7,
        last = first + 6,
        start = new Date(curr.setDate(first)).toLocaleDateString('sv-SE'),
        end = new Date(lastCurr.setDate(last)).toLocaleDateString('sv-SE'),
        { date, time } = useDate();

    const
        params = useParams(),
        [secondLevelTasks, setSecondLevelTasks] = useState([]),
        [currentIndex, setCurrentIndex] = useState(0),
        [currentBillingCode, setCurrentBillingCode] = useState(params.billingCode) /*useState(allBillingCodes[currentIndex])*/,
        [currentProject, setCurrentProject] = useState(currentBillingCode.split('.')[0]),
        [updateTime, setUpdateTime] = useState(new Date().toLocaleTimeString()),
        [isLoading, setIsLoading] = useState(true);

    const
        //{ secondLevelTasks, isLoading: workPlansIsLoading, error } = useFetchWorkPlans('1'),
        { data: milestoneData } = useSWRImmutable(`${apiBaseURL}/api/project/workplan?showDeleted=false&projectId=${currentProject}`,
            //async (url) => {
            //    try {
            //        const response = await fetch(url);
            //        //const data = await response.json();
            //        //const secondLevel = data.data.workPlans.find(x => x.headId === currentBillingCode);
            //        //console.log(secondLevel)
            //        //const
            //        //    milestoneData = {
            //        //        taskId: "2.1",
            //        //        task: "Qualification of Thermoset Materials for NCAMP Database",
            //        //        completion: 0.60,
            //        //        due: "10/5/2024"
            //        //    };

            //        //setIsLoading(false)
            //        //return milestoneData;

            //    } catch (error) {
            //        console.error('Error fetching data: ', error)
            //    }
            //}
        ),
        { data } = useSWRImmutable(`https://atlasboard.wsu-idp.org/backend/api/ResearchCosts?billingCode=${currentBillingCode}`,
            async (url) => {
                try {
                    const response = await fetch(url);
                    const data = await response.json();

                    //CREATE AN ARRAY FOR OPERATIONS HOURS 
                    //isolate workCenterName and hours into a new array
                    const rawOperationsHours = data?.operationsCostCollection.map(x => ({
                        workCenterName: x.workCenterName,
                        hours: x.hours,
                    }));
                    //sum up duplicate workCenterName entries
                    const operationsHours = rawOperationsHours.reduce((acc, entry) => {
                        const { workCenterName, hours } = entry;
                        if (acc[workCenterName]) {
                            acc[workCenterName] += hours; // Add hours if workCenterName already exists
                        } else {
                            acc[workCenterName] = hours; // Initialize hours for new workCenterName
                        }
                        return acc;
                    }, {});

                    const
                        generalData = {
                            billingCode: data?.bilingCode,
                            subTaskName: data?.subTaskName
                        },
                        costsData = [{
                            programManagementCost: data?.programManagementCostTotal,
                            operationsCost: data?.operationsCostTotal,
                            materialsCost: data?.materialsCostTotal,
                            totalCost: data?.totalCost
                        }],
                        hourData = [{
                            fulltime: data?.programManagementCollection?.fulltimeHours,
                            student: data?.programManagementCollection?.studentHours,
                            operationsHours
                        }],
                        teamData = data?.team;

                    setIsLoading(false)
                    return {
                        generalData,
                        costsData,
                        hourData,
                        teamData
                    };
                } catch (error) {
                    console.error('Error fetching data: ', error)
                }
            });


    useEffect(() => {
        //is it more efficient to create a new variable with only the data needed from secondleveltasks
        //or to just send over all the excess data to the milestones table
        const findSecondLevelTasks = milestoneData?.data?.workPlans.filter(x => x.headId === currentBillingCode);
        const condenseSecondLevelTasks = findSecondLevelTasks?.map(x => ({
            billingCode: x.billingCode,
            taskName: x.taskName,
            completedPercentage: x.completedPercentage,
            endDate: x.endDate
        }));
        setSecondLevelTasks(condenseSecondLevelTasks);
    }, [currentBillingCode, milestoneData]);

    useEffect(() => {
        const updateBillingCode = () => {
            setCurrentIndex((prevIndex) => {
                const nextIndex = (prevIndex + 1) % allBillingCodes.length; // Move to the next index or cycle back to 0
                const nextBillingCode = allBillingCodes[nextIndex]
                setCurrentBillingCode(nextBillingCode);
                setCurrentProject(nextBillingCode.split('.')[0]);
                return nextIndex;
            });
        };

        //const fadeInterval = setInterval(updateBillingCode, 10 * 1000);

        //const refreshInterval = setInterval(() => {
        //    window.location.reload(true);
        //}, 12 * 60 * 60 * 1000);//hours * minutes * seconds * milliseconds

        return () => {
            //clearInterval(fadeInterval);
            //clearInterval(refreshInterval);
        }
    }, []);



    if (!data) {
        return <LoadingSpinnerModal show />;
    } else {
        return (
            <div >
                <div>
                    <div className="yellowBG dashHeader">
                        <h1 className="black col-auto me-auto">{currentBillingCode}</h1>
                        <h4 className="black col-auto me-auto">{data?.generalData.subTaskName}</h4>
                        {/*<div className="col-auto">*/}
                        {/*    <img src={niarLogo} alt="Niar Logo" width="100" />*/}
                        {/*</div>*/}
                        {/*<div className="col-auto">*/}
                        {/*    <img src={atlasLogo} alt="Atlas Logo" width="100" />*/}
                        {/*</div>*/}
                    </div>
                </div>
                <div className="contentBox" style={{ height: '90vh' }}>
                    <div>
                        <div className="row justify-content-center">
                            <div className="research-dashboard-table">
                                <Milestones data={secondLevelTasks} />
                            </div>
                        </div>
                        <div className="row justify-content-center">
                            <div className="research-dashboard-table">
                                <Hours data={data.hourData} />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-7">
                                <div className="research-dashboard-table">
                                    <Costs data={data.costsData} />
                                </div>
                            </div>
                            <div className="col-5">
                                <div className="research-dashboard-table">
                                    <Team data={data.teamData} />
                                </div>
                            </div>
                        </div>
                        <div className="row justify-content-between">
                            <div className="timestampCurrent">
                                <h5>{date}</h5>
                                <h5>{time}</h5>
                            </div>
                            {/*<div className="timestampUpdate">*/}
                            {/*    <h5>Last Update: </h5>*/}
                            {/*    <h5> {updateTime}</h5>*/}
                            {/*</div>*/}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
